import type {
  BankTransferDTO,
  BookingSuccessDTO,
  CompleteSepaMandateDTO,
  CreateBookCollectionErrorDTO,
  CreateBookCollectionSuccessDTO,
  EndUserErrorDTO,
  PayBookCollectionDTO,
  PaymentApprovedDTO,
  PaymentCancelledDTO,
  RedirectToPayDTO,
  SepaMandateEntitiesDTO,
} from "./dto/response.dto";
import beeApi from "./bee.api";
import { handleCacheEntryAdded } from "./handleCacheEntryAdded";
import { prepareResponse } from "./prepareResponse";
import { omit } from "@/utils/object";
import { widgetApiHooks } from "@/errors/widgetApiHooks";

const beePaymentsApi = beeApi.injectEndpoints({
  endpoints: (builder) => ({
    successMessages: builder.query({
      query: (queryObject: Record<string, string>) => ({
        path: "/success",
        params: queryObject,
      }),

      transformResponse: (response: BookingSuccessDTO) => {
        return {
          outgoingMessages: response.body.outgoing_messages_response.body,
          flashNotifications: Object.values(
            response.body.flash_notifications.body,
          ).flat(),
        };
      },
    }),

    paybookEntities: builder.query({
      // A completed payment consumes this session. Its terminal response is
      // retained for navigation, but must never refetch the consumed checkout.
      providesTags: (result) =>
        result?.name === "pay_book_pay_redirect" || result?.name === "boooking_success"
          ? []
          : ["ServerData"],
      query: (params: {
        couponCodeRemoved?: boolean;
        sessionId: string;
        couponCode?: string;
        requestId?: number;
        page?: string;
      }) => ({
        path: `/paybook`,
        params: {
          payment_session_id: params.sessionId,
          ...(params.couponCodeRemoved
            ? { remove_coupon: params.couponCodeRemoved }
            : {}),
          ...(params.couponCode ? { coupon_code: params.couponCode } : {}),
        },
      }),

      transformResponse: (
        response: PayBookCollectionDTO | EndUserErrorDTO,
        _meta,
        { page },
      ) =>
        prepareResponse(response, {
          page,
          isAdminIntegration: _meta?.isAdminIntegration,
        }),
      onCacheEntryAdded: async (_arg, api) =>
        handleCacheEntryAdded(api.cacheDataLoaded, api.dispatch),
    }),

    updatePaybookCoupon: builder.mutation({
      query: (params: {
        sessionId: string;
        couponCode: string;
        removed: boolean;
        page: string;
      }) => ({
        path: "/paybook",
        params: {
          payment_session_id: params.sessionId,
          coupon_code: params.couponCode,
          ...(params.removed ? { remove_coupon: true } : {}),
        },
      }),
      transformResponse: (
        response: PayBookCollectionDTO | EndUserErrorDTO,
        meta,
        { page },
      ) =>
        prepareResponse(response, {
          page,
          isAdminIntegration: meta?.isAdminIntegration,
        }),
      async onQueryStarted(
        { sessionId, page },
        { dispatch, getState, queryFulfilled },
      ) {
        // A voucher edit supersedes any older checkout refresh for this session.
        const cancelCheckoutReads = () => {
          const requests = beePaymentsApi.util
            .selectCachedArgsForQuery(getState(), "paybookEntities")
            .filter((arg) => arg.sessionId === sessionId)
            .flatMap((arg) => {
              const request = dispatch(
                beePaymentsApi.util.getRunningQueryThunk("paybookEntities", arg),
              );
              return request ? [request] : [];
            });
          requests.forEach((request) => request.abort());
          return requests;
        };
        const previousCheckouts = cancelCheckoutReads();
        try {
          const { data } = await queryFulfilled;
          // Also supersede reads started by another invalidation during the edit.
          await Promise.all([...previousCheckouts, ...cancelCheckoutReads()]);
          // Replace the canonical checkout with the server response; no optimistic coupon state.
          await dispatch(
            beePaymentsApi.util.upsertQueryData(
              "paybookEntities",
              { sessionId, page },
              data,
            ),
          );
        } catch {
          // The caller handles the request error; preserve the last successful checkout.
        }
      },
    }),

    completePayment: builder.mutation({
      query: (params: {
        payment_session_id: string;
        selected_bill_ids: number[];
        coupon_code?: string;
        payment_method: string;
        page?: string;
      }) => ({
        path: `/paybook`,
        method: "POST",
        body: JSON.stringify(omit(params, "page")),
      }),

      transformResponse: (
        response: CreateBookCollectionSuccessDTO | RedirectToPayDTO,
        _meta,
        { page },
      ) =>
        prepareResponse(response, {
          page,
          isAdminIntegration: _meta?.isAdminIntegration,
        }),

      async onQueryStarted(
        { payment_session_id, page },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          if ("redirectUrl" in data) {
            dispatch(
              beePaymentsApi.util.updateQueryData(
                "paybookEntities",
                { sessionId: payment_session_id, page },
                () => data,
                true,
              ),
            );
          }
        } catch {
          // Failed payment leaves the checkout available for correction.
        }
      },

      onCacheEntryAdded: async (_arg, api) =>
        handleCacheEntryAdded(api.cacheDataLoaded, api.dispatch, {
          resetNotifications: true,
        }),
    }),

    paybookPaymentAwaited: builder.mutation({
      query: (params: {
        paymentId: string;
        token: string;
        payment_session_id: string;
        isApproved: boolean;
      }) => ({
        path: params.isApproved ? `/paybook/approved` : `/paybook/cancelled`,
        method: params.isApproved ? "POST" : "GET",
        params: {
          token: params.token,
          payment_session_id: params.payment_session_id,
          paymentId: params.isApproved ? params.paymentId : undefined,
        },
      }),

      transformResponse: (
        response:
          | PaymentApprovedDTO
          | PaymentCancelledDTO
          | PayBookCollectionDTO
          | EndUserErrorDTO,
        _meta,
        arg,
      ) =>
        prepareResponse(response, {
          isAdminIntegration: _meta?.isAdminIntegration,
          fallbackUrl: `#/paybook?payment_session_id=${arg.payment_session_id}`,
          replaceUrl: true,
        }),

      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          await handleCacheEntryAdded(queryFulfilled, dispatch, {
            resetNotifications: true,
          });
        } catch {
          // The return page retains the session and offers an explicit retry.
        }
      },
    }),

    bankTransferEntities: builder.query({
      query: (params: {
        book_success_id: string;
        payment_session_id: string;
        page: string;
      }) => ({
        path: `/banktransfer`,
        params: {
          book_success_id: params.book_success_id,
          payment_session_id: params.payment_session_id,
        },
      }),

      transformResponse: (
        response: EndUserErrorDTO | BankTransferDTO,
        _meta,
        { page },
      ) =>
        prepareResponse(response, {
          page,
          isAdminIntegration: _meta?.isAdminIntegration,
        }),

      onCacheEntryAdded: async (_arg, api) =>
        handleCacheEntryAdded(api.cacheDataLoaded, api.dispatch, {
          resetNotifications: true,
        }),
    }),

    sepaMandateEntities: builder.query({
      providesTags: ["ServerData"],
      query: (params: { sessionId: string; page: string }) => ({
        path: `/sepadirectdebit`,
        params: {
          payment_session_id: params.sessionId,
        },
      }),

      transformResponse: (
        response: EndUserErrorDTO | SepaMandateEntitiesDTO,
        _meta,
        { page },
      ) =>
        prepareResponse(response, {
          page,
          isAdminIntegration: _meta?.isAdminIntegration,
        }),
    }),

    completeSepaMandate: builder.mutation({
      query: (
        params: Record<string, unknown> & {
          payment_session_id: string;
          page?: string;
        },
      ) => ({
        path: `/sepadirectdebit`,
        body: JSON.stringify(omit(params, "page")),
        method: "POST",
        params: {
          payment_session_id: params.payment_session_id,
        },
      }),

      transformResponse: (
        response:
          | CompleteSepaMandateDTO
          | EndUserErrorDTO
          | CreateBookCollectionErrorDTO,
        _meta,
        { page },
      ) =>
        prepareResponse(response, {
          page,
          isAdminIntegration: _meta?.isAdminIntegration,
        }),

      onCacheEntryAdded: async (_arg, api) =>
        handleCacheEntryAdded(api.cacheDataLoaded, api.dispatch, {
          resetNotifications: true,
        }),
    }),
  }),
});

export const {
  usePaybookEntitiesQuery,
  useUpdatePaybookCouponMutation,
  usePaybookPaymentAwaitedMutation,
  useBankTransferEntitiesQuery,
  useSepaMandateEntitiesQuery,

  useSuccessMessagesQuery,

  useCompletePaymentMutation,
  useCompleteSepaMandateMutation,
} = widgetApiHooks(beePaymentsApi);

export default beePaymentsApi;
